<template lang="pug">
.chart-line
  svg(
    :width="width"
    :height="height"
  )
</template>

<script>
import * as d3 from 'd3';
import { isEqual, isNil } from 'lodash-es';

export default {
  name: 'AppChartLine',
  props: {
    values: {
      type: Array,
      required: true,
    },
    width: {
      type: Number,
      default: 100,
    },
    height: {
      type: Number,
      default: 25,
    },
    lineClass: {
      type: String,
    },
  },
  computed: {
    hasValidDataPoints() {
      // Validate if we have AT LEAST one valid data point (y axis having a number).
      // We need this validation so that the chart can render correctly, so we count how many
      // valid points exist before rendering.
      return !!(this.values.length - this.values.filter((v) => isNil(v.y)).length);
    },
  },
  watch: {
    values(n, o) {
      if (!isEqual(n, o)) {
        this.buildChart();
      }
    },
  },
  mounted() {
    this.buildChart();
  },
  methods: {
    buildChart() {
      // If the number of "invalid" points is the same as the total number of points,
      // the chart will skip rendering.
      if (!this.hasValidDataPoints) {
        return;
      }

      // Clear any chart information before building.
      // We do this so that the chart is correctly redrawn when values change.
      d3.select(this.$el).select('svg > *').remove();

      // This margin ensures that the line thickness
      // doesn't get cut off when it reaches the chart boundaries
      const margin = 3;
      const block = d3.select(this.$el).select('svg').append('g');
      // Set up the axis:
      // x axis goes from 0 to `width`
      // y axis ranges between the min and max point.y values
      const x = d3
        .scaleTime()
        .domain(d3.extent(this.values, (point) => point.x))
        .range([0, this.width - margin]);
      const y = d3
        .scaleLinear()
        .domain([d3.min(this.values, (point) => point.y), d3.max(this.values, (point) => point.y)])
        .range([this.height - margin, margin]);
      const lineClass = this.lineClass ? ` ${this.lineClass}` : '';

      block
        .append('g')
        .attr('transform', 'translate(0,' + this.height + ')')
        .call(d3.axisBottom(x))
        .select('.domain')
        .remove();
      block.append('g').call(d3.axisLeft(y)).select('.domain').remove();

      // Set up the canvas path with the values
      block
        .append('path')
        .datum(this.values)
        .attr('class', `line${lineClass}`)
        .attr(
          'd',
          d3
            .line()
            .x((point) => x(point.x))
            .y((point) => y(point.y)),
        );
    },
  },
};
</script>
